import { useState } from 'react';

import { useMutation, useQuery } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { ImageIcon, Loader2, Pen, Plus, Trash, X } from 'lucide-react';
import { useDropzone } from 'react-dropzone';
import { useFormContext, useFormState } from 'react-hook-form';

import InputControl from '@/components/form/input-control';
import SelectControl from '@/components/form/select-control';
import { Button } from '@/components/ui/button';
import { DataTable } from '@/components/ui/data-table';
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogTitle
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import PreviewFileDialog from '@/components/ui/preview-file';
import { WithStateQuery } from '@/components/ui/with-state';
import { useConfirmation } from '@/hooks/use-confirm-dialog';
import { useResponse } from '@/hooks/use-dialog';
import { cn } from '@/lib/utils';
import HookFormProvider from '@/providers/hook-form-providers';
import { fileMutations, fileQueries } from '@/queries/file/file.queries';
import { masterQueries } from '@/queries/master/master.queries';
import {
	vendorMutations,
	vendorQueries
} from '@/queries/vendor/vendor.queries';
import { noOp } from '@/services/fn';
import { extractFileParts, onFileRejected } from '@/services/string';
import { useDialogStore } from '@/store';
import {
	bankDataSchema,
	type BankDataSchema
} from '@/types/form/account-manager/form';
import type { DataTableResponse } from '@/types/response';
import type { VendorBankDataResponse } from '@/types/vendor/vendor';

const MAX_BANK_DATA = 3;

export function OpenFile({
	fileKey,
	label
}: Readonly<{ fileKey: string, label: string }>) {
	const setDialog = useDialogStore((s) => s.setStore);
	const setAction = useDialogStore((s) => s.setAction);
	const onOpen = useDialogStore((s) => s.onOpenChange);

	const fileMutation = useMutation(fileMutations.getPresign);

	function openFile() {
		fileMutation.mutate(
			{ key: fileKey },
			{
				onSuccess: ({ data: { url } }) => {
					globalThis.open(url, '_blank');
				},
				onError: ({ message }) => {
					setDialog('variant', 'error');
					setDialog('title', 'Gagal');
					setDialog('description', message);
					setDialog('confirmText', 'Ok');
					setDialog('cancelText', null);
					setAction('onConfirm', () => onOpen(false));
				}
			}
		);
	}
	return (
		<Button
			size={'sm'}
			onClick={openFile}
			className="h-auto w-full justify-start p-0 text-left whitespace-normal"
			type="button"
			variant={'link'}
		>
			{label}
		</Button>
	);
}

function Delete({ id }: Readonly<{ id: number }>) {
	const setDialog = useDialogStore((s) => s.setStore);
	const setAction = useDialogStore((s) => s.setAction);
	const onOpen = useDialogStore((s) => s.onOpenChange);

	const action = useMutation(vendorMutations.bankDataDelete(id));

	return (
		<Button
			variant={'outline'}
			size={'icon'}
			className="border-destructive text-destructive hover:text-destructive/50 hover:border-destructive/70 w-20 border-2 text-sm"
			onClick={() => {
				setDialog('variant', 'warning');
				setDialog('confirmText', 'Ya, Hapus');
				setDialog('title', 'Hapus Data?');
				setDialog('description', 'Apakah Anda yakin ingin menghapus data bank?');
				setDialog('cancelText', 'Batal');
				setAction('onCancel', () => onOpen(false));
				setAction('onConfirm', () =>
					action.mutate(undefined, {
						onSuccess: () => {
							setDialog('variant', 'success');
							setDialog('confirmText', 'OK');
							setDialog('title', 'Berhasil');
							setDialog('description', 'Data Bank Berhasil Dihapus');
							setDialog('cancelText', null);
							setAction('onConfirm', () => onOpen(false));
							onOpen(true);
						},
						onError: ({ message }) => {
							setDialog('variant', 'error');
							setDialog('title', 'Gagal');
							setDialog('description', message);
							setDialog('confirmText', 'OK');
							onOpen(true);
							setAction('onConfirm', () => onOpen(false));
						}
					})
				);
				onOpen(true);
			}}
		>
			<Trash />
			Hapus
		</Button>
	);
}

const columns = ({
	handleUpdate,
	isDisabled
}: {
	handleUpdate: (data: BankDataSchema, id: number) => void,
	isDisabled?: boolean
}): ColumnDef<VendorBankDataResponse>[] => [
	{
		accessorFn: (_, i) => i.toString(),
		accessorKey: 'no',
		header: 'No',
		cell: ({ row, table: { getState } }) =>
			row.index +
			1 +
			getState().pagination.pageIndex * getState().pagination.pageSize,
		meta: { className: 'w-4 text-center', id: 'no' }
	},
	{
		accessorFn: (r) => r.bankCode,
		accessorKey: 'bankCode',
		header: 'Kode Bank',
		cell: ({ row: { original } }) => original.bankCode,
		meta: { id: 'bankCode' }
	},
	{
		accessorFn: (r) => r.bankName,
		accessorKey: 'bankName',
		header: 'Nama Bank',
		cell: ({ row: { original } }) => original.bankName,
		meta: { id: 'bankName' }
	},
	{
		accessorFn: (r) => r.accountName,
		accessorKey: 'accountName',
		header: 'Nama Rekening',
		cell: ({ row: { original } }) => original.accountName,
		meta: { id: 'accountName' }
	},
	{
		accessorFn: (r) => r.accountNumber,
		accessorKey: 'accountNumber',
		header: 'Nomor Rekening',
		cell: ({ row: { original } }) => original.accountNumber,
		meta: { id: 'accountNumber' }
	},
	{
		accessorFn: (r) => r.image,
		accessorKey: 'image',
		header: 'Buku Tabungan',
		cell: ({ row: { original } }) => (
			<PreviewFileDialog fileKey={original.image} label="Lihat Detail" />
		),
		meta: { id: 'image' }
	},
	{
		accessorFn: () => 'actions',
		accessorKey: 'actions',
		header: 'Aksi',
		cell: ({ row: { original } }) => (
			<div className="flex gap-2 py-2">
				<Button
					variant={'outline'}
					size={'icon'}
					className="w-20 border-2 text-sm"
					onClick={() =>
						handleUpdate(
							{
								accountName: original.accountName,
								accountNumber: original.accountNumber,
								bankId: original.bankId,
								image: original.image
							},
							original.id
						)
					}
				>
					<Pen />
					Ubah
				</Button>
				<Delete id={original.id} />
			</div>
		),
		meta: {
			id: 'actions',
			isVisible: !isDisabled
		}
	}
];

function Bank() {
	const options = useQuery(masterQueries.listBank({ showAll: true }));

	return (
		<SelectControl
			name="bankId"
			options={
				options.data
					? options.data.data.map((v) => ({
							...v,
							label: v.code.concat(' - ', v.name),
							value: v.id
						}))
					: []
			}
			label="Kode Bank"
			isLoading={options.isPending}
			isSearchable
		/>
	);
}

function Image({ fileKey }: Readonly<{ fileKey: string }>) {
	return (
		<img
			src={fileKey}
			alt="NPWP Preview"
			className="max-h-full max-w-full rounded-md object-contain shadow-sm"
		/>
	);
}

function PreviewFile({
	fileKey,
	isDirectUrl = false
}: Readonly<{ fileKey: string, isDirectUrl?: boolean }>) {
	const file = useQuery({
		...fileQueries.getPresign({ key: fileKey }),
		enabled: !isDirectUrl
	});

	if (isDirectUrl) {
		return <Image fileKey={fileKey} />;
	}
	return (
		<WithStateQuery state={file}>
			{({ data: { url } }) => <Image fileKey={url} />}
		</WithStateQuery>
	);
}
function FileUpload({
	vendorName,
	image
}: Readonly<{ vendorName: string, image: string | null }>) {
	const { setValue, getValues } = useFormContext<BankDataSchema>();
	const { errors } = useFormState<BankDataSchema>({ name: 'image' });
	const { isDirty } = useFormState<BankDataSchema>({ name: 'image' });

	const [file, setFile] = useState(getValues('image'));
	const [visible, setVisible] = useState<boolean>(false);
	const [progress, setProgress] = useState<number>(0);
	const [previewUrl, setPreviewUrl] = useState<{
		key: string | null,
		isDirectUrl: boolean
	}>({ key: file, isDirectUrl: false });

	const onOpen = useDialogStore((s) => s.onOpenChange);
	const { askCloseIfDirty } = useConfirmation();
	const { onError } = useResponse();

	const uploadMutation = useMutation(fileMutations.uploadPresign);

	const { getRootProps, getInputProps, open, isDragAccept } = useDropzone({
		accept: {
			'image/png': ['.png'],
			'image/jpeg': ['.jpg', '.jpeg']
		},
		disabled: uploadMutation.isPending,
		noKeyboard: true,
		onDropAccepted: (acceptedFiles) => {
			const file = acceptedFiles[0];
			if (!file) return;

			setPreviewUrl({ key: null, isDirectUrl: false });
			setProgress(0);

			const date = new Date();
			const { fileName, fileExt } = extractFileParts(file.name);
			const fileKey = `${vendorName}/${date.getFullYear()}/${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getDate().toString().padStart(2, '0')}/${crypto.randomUUID()}/${fileName.slice(0, 50)}.${fileExt}`;

			uploadMutation.mutate(
				{
					key: fileKey,
					file: file,
					onProgress: (val) => setProgress(val)
				},
				{
					onSuccess: (sasUrl) => {
						setPreviewUrl({ key: sasUrl, isDirectUrl: true });
						setFile(fileKey);
					},
					onError: noOp
				}
			);
		},
		noClick: true,
		multiple: false,
		maxSize: 5_242_880,
		onDropRejected: (err) => {
			const errFile = err[0];
			if (!errFile) return;

			onError({
				message: onFileRejected({ code: errFile.errors[0]?.code, type: 'image' }),
				confirmText: 'Coba Lagi'
			});
		}
	});

	const handleReset = ({
		reset,
		isCreated
	}: {
		reset?: boolean,
		isCreated?: boolean
	}) => {
		const file = isCreated ? getValues('image') : image;
		setFile(reset ? null : file);
		setPreviewUrl({ key: reset ? null : file, isDirectUrl: false });
		setProgress(0);
		uploadMutation.reset();
	};

	const handleSave = () => {
		setValue('image', file, { shouldDirty: true, shouldValidate: true });
		setVisible(false);
	};

	return (
		<>
			<div className="flex w-fit flex-col gap-2">
				<Label className="text-label-input text-xs">Buku Tabungan</Label>
				<Button
					variant={'outline'}
					className="flex h-12 items-start"
					onClick={() => setVisible(true)}
					type="button"
				>
					<img src="/icon/upload.webp" alt="icon-upload" className="size-4" />
					<span className="flex flex-col items-start">
						<p className="text-xs text-black">
							{previewUrl.key ? 'Lihat Dokumen (1)' : 'Unggah Dokumen'}
						</p>
						<p className="text-secondary text-xs font-light">
							{previewUrl.key
								? 'Lihat atau unggah ulang dokumen'
								: 'Dokumen: JPG,JPEG, PNG (maks. 5MB)'}
						</p>
					</span>
				</Button>
				{errors.image?.message ? (
					<p
						data-slot="form-message"
						className={cn('text-destructive -mt-2 text-sm')}
					>
						{errors.image?.message}
					</p>
				) : null}
			</div>
			<Dialog
				open={visible}
				onOpenChange={(v) => {
					if (!v) {
						askCloseIfDirty(
							isDirty || file !== image,
							() => {},
							() => {
								handleReset({ isCreated: image === null });
								setVisible(false);
								onOpen(false);
							}
						);
					}
				}}
			>
				<DialogContent
					onPointerDownOutside={(e) =>
						askCloseIfDirty(
							isDirty || file !== image,
							() => e.preventDefault(),
							() => {
								handleReset({ isCreated: image === null });
								setVisible(false);
								onOpen(false);
							}
						)
					}
					onEscapeKeyDown={(e) =>
						askCloseIfDirty(
							isDirty || file !== image,
							() => e.preventDefault(),
							() => {
								handleReset({ isCreated: image === null });
								setVisible(false);
								onOpen(false);
							}
						)
					}
				>
					<DialogTitle className="flex flex-col">
						<span className="text-lg text-black">Buku Tabungan</span>
						<span className="text-label-input">
							Silakan unggah Buku Tabungan Anda
						</span>
					</DialogTitle>
					<DialogDescription />
					<div
						{...getRootProps({
							className: cn(
								'dropzone border-2 border-slate-300 border-dashed h-36 flex flex-col gap-1 justify-center items-center p-8 text-center rounded-lg transition delay-100 duration-300 ease-in-out',
								isDragAccept && 'bg-slate-400/50'
							)
						})}
					>
						<input {...getInputProps()} />

						{uploadMutation.isPending ? (
							<div className="z-10 flex flex-col items-center gap-2">
								<Loader2 className="size-8 animate-spin text-blue-500" />
								<span className="text-sm font-medium">Uploading... {progress}%</span>
							</div>
						) : null}

						{!uploadMutation.isPending && previewUrl.key ? (
							<div className="relative flex h-full w-full items-center justify-center p-2">
								<PreviewFile
									fileKey={previewUrl.key}
									isDirectUrl={previewUrl.isDirectUrl}
								/>
								<button
									onClick={(e) => {
										e.stopPropagation();
										handleReset({ reset: true, isCreated: image === null });
									}}
									className="absolute -top-2 -right-2 rounded-full border bg-white p-1 shadow-md transition hover:bg-gray-100"
								>
									<X className="size-4 text-gray-500" />
								</button>
							</div>
						) : (
							/* 3. IDLE STATE */
							!uploadMutation.isPending && (
								<>
									<div
										className="flex w-full cursor-pointer items-center justify-center rounded bg-neutral-50 p-4 transition hover:bg-neutral-100"
										onClick={open}
										role="none"
									>
										<ImageIcon className="size-6 text-gray-400" />
									</div>
									<span className="mt-4 font-medium text-black">
										Unggah Buku Tabungan
									</span>
									<span className="text-secondary mt-1 text-xs">
										Unggah file JPG, JPEG, PNG (maks. 5MB)
									</span>
								</>
							)
						)}
					</div>
					<DialogFooter className="shrink-0 sm:justify-end">
						<Button
							type="button"
							disabled={uploadMutation.isPending}
							onClick={handleSave}
						>
							Simpan
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	);
}

function FormBankData({
	accountName = null,
	accountNumber = null,
	bankId = null,
	image = null,
	id,
	handleCloseDialog,
	vendorName
}: Readonly<
	BankDataSchema & {
		id?: number,
		handleCloseDialog: () => void,
		vendorName: string
	}
>) {
	const create = useMutation(vendorMutations.bankData);
	const update = useMutation(vendorMutations.bankDataUpdate(id));

	const { onSuccess, onError } = useResponse();

	const handleSubmit = (data: BankDataSchema) => {
		if (id) {
			update.mutate(data, {
				onSuccess: () =>
					onSuccess({
						message: 'Data Bank Berhasil Diubah',
						onConfirm: handleCloseDialog
					}),
				onError: ({ message }) => onError({ message })
			});

			return;
		}

		create.mutate(data, {
			onSuccess: () =>
				onSuccess({
					message: 'Data Bank Berhasil Ditambahkan',
					onConfirm: handleCloseDialog
				}),
			onError: ({ message }) => onError({ message })
		});
	};

	return (
		<HookFormProvider
			schema={bankDataSchema}
			defaultValues={{
				accountName,
				accountNumber,
				bankId,
				image
			}}
			onSubmit={handleSubmit}
			className="my-2"
			formOptions={{
				mode: 'onTouched'
			}}
		>
			<div className="grid w-full grid-cols-1 gap-x-4 gap-y-2">
				<Bank />
				<InputControl name="accountNumber" label="Nomor Rekening" required />
				<InputControl name="accountName" label="Nama Rekening" required />
				<FileUpload vendorName={vendorName} image={image} />
			</div>
			<div className="flex w-full justify-end">
				<Button>Simpan</Button>
			</div>
		</HookFormProvider>
	);
}

export default function FormBank({
	isDisabled = false,
	vendorName,
	banks
}: Readonly<{
	isDisabled?: boolean,
	vendorName: string,
	banks?: DataTableResponse<VendorBankDataResponse>
}>) {
	const [visible, setVisible] = useState<{
		open: boolean,
		data: BankDataSchema,
		id?: number
	}>({
		open: false,
		data: {
			accountName: null,
			accountNumber: null,
			bankId: null,
			image: null
		}
	});

	const bankData = useQuery({
		...vendorQueries.bankData(),
		enabled: !banks
	});

	const handleOpenDialog = (data: BankDataSchema, id?: number) => {
		setVisible({ open: true, id, data });
	};

	const handleCloseDialog = () =>
		setVisible((prev) => ({ ...prev, open: false }));

	return (
		<>
			<div className="my-2 flex flex-col gap-2">
				<div className="flex items-center justify-between">
					<p className="col-span-full text-xl font-medium">Informasi Bank</p>
					<WithStateQuery state={bankData}>
						{({ count }) =>
							count < MAX_BANK_DATA && !isDisabled ? (
								<Button
									type="button"
									onClick={() =>
										handleOpenDialog({
											accountName: null,
											accountNumber: null,
											bankId: null,
											image: null
										})
									}
								>
									<Plus />
									Tambah Data
								</Button>
							) : null
						}
					</WithStateQuery>
				</div>

				<DataTable
					columns={columns({ handleUpdate: handleOpenDialog, isDisabled })}
					data={banks?.data ?? bankData.data?.data}
					isLoading={banks ? false : bankData.isPending}
					total={banks ? banks.count : bankData.data?.count}
					isUsePagination={false}
				/>
			</div>
			<Dialog
				open={visible.open}
				onOpenChange={() =>
					setVisible((prev) => ({
						...prev,
						open: false
					}))
				}
			>
				<DialogContent>
					<DialogTitle className="flex flex-col">
						<span className="text-lg text-black">Informasi Bank</span>
						<span className="text-label-input">Silahkan Isi Data Bank Anda</span>
					</DialogTitle>
					<FormBankData
						{...visible.data}
						id={visible.id}
						handleCloseDialog={handleCloseDialog}
						vendorName={vendorName}
					/>
				</DialogContent>
			</Dialog>
		</>
	);
}
